import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { VideoLink } from "./VideoModal";
import {
  Loader2, Plus, Trash2, ChevronDown, ChevronRight, Pencil, Save, X as XIcon,
  Sunrise, Sunset, Coffee, Dumbbell, Crosshair, BookMarked, Copy, Library, GripVertical, Download, Link2, Youtube,
} from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/permissions";
import { useTagTree, TagEditor, LIB_COLOR, BUTTON_OF_LIBRARY, QUAL_COLOR } from "./filmdb/FilmDbTags";

const LIB_OF_BUTTON = Object.fromEntries(Object.entries(BUTTON_OF_LIBRARY).map(([lib, b]) => [b, lib]));
// Focus chips for a routine: its tags resolved against the tag tree → [{id, label, color}] (interactions as Section › Interaction).
function useFocus() {
  const tree = useTagTree();
  return useCallback((tags = []) => {
    const names = tree.names || {};
    const qualOf = tree.qualifier_of || {};
    const out = [];
    tags.forEach(t => {
      if (qualOf[t]) { out.push({ id: t, label: `${names[qualOf[t]] || ""} › ${names[t] || t}`, color: QUAL_COLOR, lib: "Outcome / Base" }); return; }
      const b = tree.buttons.find(x => x.sections.some(sec => sec.id === t || sec.interactions.some(i => i.id === t)));
      if (!b) return;
      const sec = b.sections.find(sec => sec.id === t) || b.sections.find(sec => sec.interactions.some(i => i.id === t));
      const isInter = sec.id !== t;
      if (!isInter && tags.some(o => sec.interactions.some(i => i.id === o))) return;
      out.push({ id: t, label: isInter ? `${sec.name} › ${names[t] || t}` : sec.name, color: LIB_COLOR[LIB_OF_BUTTON[b.button]] || "#a0a0a0", lib: b.button });
    });
    return out;
  }, [tree]);
}
const FocusChips = ({ focus, testId }) => focus.length ? (
  <div className="flex flex-wrap gap-1 mt-1" data-testid={testId}>
    {focus.map(f => <span key={f.id} className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 border" style={{ color: f.color, borderColor: `${f.color}66` }} title={f.lib}>{f.label}</span>)}
  </div>
) : null;

/** Fixed routine-type ordering + presentation. */
const ROUTINE_TYPES = [
  { key: "pre_practice",  label: "Pre-Practice",   Icon: Sunrise,  accent: "#d8b854" },
  { key: "post_practice", label: "Post-Practice",  Icon: Sunset,   accent: "#c084fc" },
  { key: "off_light",     label: "Off Days · Light", Icon: Coffee,  accent: "#60a5fa" },
  { key: "off_full",      label: "Off Days · Full",  Icon: Dumbbell, accent: "#f59e0b" },
  { key: "shooting",      label: "Shooting Routines", Icon: Crosshair, accent: "#d8b854" },
];
// The Shooting Routine lives at the end of the Shooting Assessment (onlyType="shooting"); the main Routines block hides it.
const ACCENT = "#d8b854";

const UNIT_OPTIONS = [
  { value: "reps",       label: "Makes",     placeholder: "50" },
  { value: "seconds",    label: "Seconds",  placeholder: "45" },
  { value: "minutes",    label: "Minutes",  placeholder: "3" },
  { value: "sets_reps",  label: "Sets × Makes", placeholder: "3x10" },
];

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function formatUnit(unit, value) {
  if (!value) return "—";
  const short = { reps: "makes", seconds: "sec", minutes: "min", sets_reps: "" };
  const suffix = short[unit] ?? "";
  return suffix ? `${value} ${suffix}` : value;
}

function blankDrill() {
  return { id: null, name: "", description: "", video_url: "", unit: "reps", value: "" };
}


export default function PlayerRoutines({ playerId, playerName, onlyType = null, embedded = false }) {
  const { user } = useAuth();
  const focusOf = useFocus();
  const canEdit = can(user, "training.routines_edit");
  const canManageTemplates = can(user, "training.routine_templates_edit");
  const [loading, setLoading] = useState(true);
  const [byType, setByType] = useState({});
  const [expanded, setExpanded] = useState({});
  const [editingId, setEditingId] = useState(null);      // routine.id or `new:${type}`
  const [draft, setDraft] = useState(null);              // {routine_type, label, drills[]}
  const [saving, setSaving] = useState(false);

  // Template picker
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templatesForType, setTemplatesForType] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listPlayerRoutines(playerId);
      setByType(r.by_type || {});
    } catch { toast.error("Failed to load routines"); }
    finally { setLoading(false); }
  }, [playerId]);

  useEffect(() => { load(); }, [load]);

  const startNew = (routineType) => {
    setEditingId(`new:${routineType}`);
    setDraft({ routine_type: routineType, label: "", video_url: "", tags: [], drills: [blankDrill()] });
    setExpanded(prev => ({ ...prev, [routineType]: true }));
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setDraft({
      routine_type: r.routine_type,
      label: r.label,
      video_url: r.video_url || "",
      tags: Array.isArray(r.tags) ? r.tags : [],
      drills: (r.drills || []).map(d => ({ ...d })).concat([blankDrill()]),
    });
  };

  const cancelEdit = () => { setEditingId(null); setDraft(null); };

  const updateDraftDrill = (i, patch) => {
    setDraft(d => {
      const drills = d.drills.map((x, idx) => idx === i ? { ...x, ...patch } : x);
      // Auto-append a trailing blank row when the last row gets a name
      const last = drills[drills.length - 1];
      if (last && last.name.trim()) drills.push(blankDrill());
      return { ...d, drills };
    });
  };
  const removeDraftDrill = (i) => setDraft(d => ({ ...d, drills: d.drills.filter((_, idx) => idx !== i) }));

  const saveDraft = async () => {
    if (!draft?.label.trim()) { toast.error("Label required"); return; }
    // Strip blank drill rows
    const cleaned = { ...draft, drills: draft.drills.filter(x => x.name.trim()) };
    setSaving(true);
    try {
      if (editingId?.startsWith("new:")) {
        await api.createPlayerRoutine(playerId, cleaned);
      } else {
        await api.updatePlayerRoutine(playerId, editingId, cleaned);
      }
      await load();
      setEditingId(null); setDraft(null);
      toast.success("Routine saved");
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.label}"?`)) return;
    try { await api.deletePlayerRoutine(playerId, r.id); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  const saveAsTemplate = async (r) => {
    const suggested = r.label;
    const label = window.prompt(`Save "${r.label}" to the Routine Library. Name it:`, suggested);
    if (label === null) return;
    try {
      await api.saveRoutineAsTemplate(r.id, label.trim() || suggested);
      toast.success("Saved to the Routine Library", { description: `"${label.trim() || suggested}" is now in the org-wide library with its tags.`, action: { label: "Open library", onClick: () => window.open("/routine-library", "_blank") } });
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
  };

  const applyTemplate = async (templateId) => {
    try {
      await api.copyTemplateToPlayer(playerId, templateId);
      setTemplatesOpen(false);
      await load();
      toast.success("Template copied to player");
    } catch (e) { toast.error("Copy failed", { description: e?.response?.data?.detail || e.message }); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#a0a0a0] text-sm py-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading routines…
      </div>
    );
  }

  const flat = Object.values(byType).flat().filter(r => r.routine_type !== "shooting").sort((x, y) => (y.created_at || "").localeCompare(x.created_at || ""));

  const renderList = (section) => {
    const routines = byType[section.key] || [];
    const { accent } = section;
    return (
      <>
        {routines.map(r => editingId === r.id ? (
          <RoutineEditor key={r.id} draft={draft} setDraft={setDraft} onSave={saveDraft} onCancel={cancelEdit} saving={saving} accent={accent} updateDraftDrill={updateDraftDrill} removeDraftDrill={removeDraftDrill} />
        ) : (
          <RoutineCard key={r.id} r={r} accent={accent} canEdit={canEdit} playerName={playerName} playerId={playerId} sectionLabel={section.label} focus={focusOf(r.tags)} embedded={embedded} onEdit={() => startEdit(r)} onDelete={() => remove(r)} canSaveTemplate={canManageTemplates} onSaveTemplate={() => saveAsTemplate(r)} />
        ))}
        {editingId === `new:${section.key}` && (
          <RoutineEditor draft={draft} setDraft={setDraft} onSave={saveDraft} onCancel={cancelEdit} saving={saving} accent={accent} updateDraftDrill={updateDraftDrill} removeDraftDrill={removeDraftDrill} />
        )}
      </>
    );
  };

  // Embedded: a sub-block inside the Shooting Assessment card (no outer card / header of its own).
  if (embedded) {
    const section = ROUTINE_TYPES.find(s => s.key === onlyType);
    const routines = byType[section.key] || [];
    return (
      <div data-testid={`player-routines-${onlyType}`}>
        <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="font-heading uppercase text-lg text-white">Shooting Routine</div>
            <div className="text-xs text-[#a0a0a0]">Drills that reinforce the Shooting Keys</div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2 pdf-hide">
              <button onClick={() => startNew(section.key)} className="text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-1 border" style={{ color: section.accent, borderColor: `${section.accent}55`, background: `${section.accent}12` }} data-testid={`add-routine-${section.key}`}>
                <Plus className="w-3 h-3 inline mr-0.5" /> New
              </button>
              <button onClick={() => { setTemplatesForType(section.key); setTemplatesOpen(true); }} className="text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-1 border border-white/10 text-[#a0a0a0] hover:text-white" data-testid={`add-from-template-${section.key}`}>
                <Copy className="w-3 h-3 inline mr-0.5" /> From Template
              </button>
            </div>
          )}
        </div>
        <div className="space-y-3">
          {routines.length === 0 && editingId !== `new:${section.key}` && (
            <div className="border-2 border-dashed p-4 text-sm text-[#757575]" style={{ borderColor: `${section.accent}55` }} data-testid="shooting-routine-empty">
              No shooting routine yet.{canEdit ? " Build one or pull it from the template library." : ""}
            </div>
          )}
          {renderList(section)}
        </div>
        {templatesOpen && <TemplateLibraryDialog onClose={() => setTemplatesOpen(false)} onApplyToPlayer={applyTemplate} canEdit={canManageTemplates} focusType={templatesForType} />}
      </div>
    );
  }

  return (
    <section className="border border-white/10 bg-[#171717]" data-testid={embedded ? `player-routines-${onlyType}` : "player-routines"}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]">{embedded ? "From the Shooting Assessment" : "Training Routines"}</div>
          <div className="font-heading text-2xl uppercase tracking-tight text-white">{embedded ? `${playerName || "Player"}'s Shooting Routine` : `${playerName || "Player"}'s Routines`}</div>
          {embedded && <div className="text-[10px] uppercase tracking-widest text-[#757575] mt-0.5">Drills that reinforce the Shooting Keys · build it or pull one from the routine library</div>}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => startNew("pre_practice")} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-[#121212] bg-[var(--org-accent)] hover:bg-[var(--org-accent-hover)]" data-testid="add-routine-btn">
              <Plus className="w-3 h-3" /> New Routine
            </button>
            <button
              onClick={() => { setTemplatesOpen(true); setTemplatesForType(onlyType); }}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-[10px] uppercase tracking-[0.3em] font-bold text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]"
              data-testid="open-template-library-btn"
            >
              <Library className="w-3 h-3" /> From Routine Library
            </button>
          </div>
        )}
      </div>

      <div className="px-5 py-5 space-y-3" data-testid="routine-list">
        {flat.length === 0 && !editingId?.startsWith("new:") && (
          <div className="text-[12px] italic text-[#575757] px-3 py-4 border border-dashed border-white/10" data-testid="routine-list-empty">
            No routines yet.{canEdit ? " Click New Routine or pull one from the Routine Library." : ""}
          </div>
        )}
        {flat.map(r => editingId === r.id ? (
          <RoutineEditor key={r.id} draft={draft} setDraft={setDraft} onSave={saveDraft} onCancel={cancelEdit} saving={saving} accent={ACCENT} updateDraftDrill={updateDraftDrill} removeDraftDrill={removeDraftDrill} />
        ) : (
          <RoutineCard key={r.id} r={r} accent={ACCENT} canEdit={canEdit} playerName={playerName} playerId={playerId} focus={focusOf(r.tags)} onEdit={() => startEdit(r)} onDelete={() => remove(r)} canSaveTemplate={canManageTemplates} onSaveTemplate={() => saveAsTemplate(r)} />
        ))}
        {editingId?.startsWith("new:") && (
          <RoutineEditor draft={draft} setDraft={setDraft} onSave={saveDraft} onCancel={cancelEdit} saving={saving} accent={ACCENT} updateDraftDrill={updateDraftDrill} removeDraftDrill={removeDraftDrill} />
        )}
      </div>

      {templatesOpen && (
        <TemplateLibraryDialog
          onClose={() => setTemplatesOpen(false)}
          onApplyToPlayer={applyTemplate}
          canEdit={canManageTemplates}
          focusType={templatesForType}
        />
      )}
    </section>
  );
}

/* ---------------- Routine card (read-only) ---------------- */
function RoutineCard({ r, accent, canEdit, canSaveTemplate = canEdit, playerName, playerId, sectionLabel, focus = [], embedded = false, onEdit, onDelete, onSaveTemplate }) {
  const printRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState(embedded);

  const exportPDF = async () => {
    setExporting(true);
    // Build an offscreen "print sheet" — clean light layout with photo + name + routine + drills.
    // Rendered off-DOM (fixed left:-9999px) so it doesn't disrupt the live UI.
    const sheet = document.createElement("div");
    sheet.style.cssText = "position:fixed;left:-9999px;top:0;width:800px;background:#232323;color:#ffffff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;padding:0;";
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    // Fetch the headshot as a data URL so html2canvas can render it without any CORS/timing issues.
    let photoSrc = "";
    if (playerId) {
      try {
        const resp = await fetch(`${backend}/api/players/${playerId}/headshot`, { credentials: "include" });
        if (resp.ok) {
          const blob = await resp.blob();
          photoSrc = await new Promise(res => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.readAsDataURL(blob);
          });
        }
      } catch (_) { /* no photo — render without it */ }
    }
    // Maximize brand palette (overrides per-section accent in the PDF only)
    const BG      = "#232323"; // brand dark
    const PANEL   = "#2c2c2c"; // slightly lighter panel
    const CHIP_BG = "#1a1a1a"; // drill number chip background
    const BLUE    = "#3aa0ff"; // primary accent
    const GOLD    = "#d8b854"; // secondary accent (used for scoring pill)
    const WHITE   = "#ffffff";
    const MUTED   = "#a0a0a0";
    const HAIR    = "rgba(255,255,255,0.10)";

    const drillsHtml = (r.drills || []).map((d, i) => `
      <div style="display:flex;align-items:stretch;background:${PANEL};margin-bottom:8px;border-left:3px solid ${BLUE};overflow:hidden;">
        <div style="width:54px;display:flex;align-items:center;justify-content:center;background:${CHIP_BG};font-family:'SFMono-Regular',Consolas,monospace;color:${BLUE};font-size:20px;font-weight:900;letter-spacing:-1px;">${String(i + 1).padStart(2, "0")}</div>
        <div style="flex:1;padding:14px 18px;">
          <div style="font-weight:800;font-size:15px;color:${WHITE};letter-spacing:-0.2px;line-height:1.15;">${escapeHtml(d.name || "")}</div>
          ${d.description ? `<div style="font-size:12px;color:${MUTED};margin-top:4px;line-height:1.4;">${escapeHtml(d.description)}</div>` : ""}
          ${d.video_url ? `<div style="font-size:10px;color:${GOLD};margin-top:5px;letter-spacing:0.1em;font-weight:700;">▶ VIDEO: <span style="color:${MUTED};font-weight:400;letter-spacing:0;">${escapeHtml(d.video_url)}</span></div>` : ""}
        </div>
        <div style="padding:14px 18px;display:flex;align-items:center;justify-content:flex-end;min-width:140px;">
          <span style="display:inline-block;background:${GOLD};color:${BG};font-weight:900;font-size:11px;letter-spacing:0.15em;text-transform:uppercase;padding:6px 12px;white-space:nowrap;">${escapeHtml(formatUnit(d.unit, d.value))}</span>
        </div>
      </div>
    `).join("");
    const drillCount = (r.drills || []).length;
    sheet.innerHTML = `
      <div style="background:${BG};color:${WHITE};padding:44px 40px 32px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">

        <!-- Brand strip -->
        <div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:24px;border-bottom:1px solid ${HAIR};">
          <div style="display:flex;align-items:center;gap:10px;">
            <div style="width:32px;height:32px;background:${GOLD};display:flex;align-items:center;justify-content:center;font-weight:900;font-size:16px;color:${BG};">M</div>
            <div>
              <div style="font-size:9px;letter-spacing:0.4em;text-transform:uppercase;color:${GOLD};font-weight:900;line-height:1;">Maximize</div>
              <div style="font-size:10px;letter-spacing:0.25em;text-transform:uppercase;color:${MUTED};font-weight:700;margin-top:3px;line-height:1;">Analytics &amp; Development</div>
            </div>
          </div>
          <div style="font-size:10px;letter-spacing:0.25em;text-transform:uppercase;color:${MUTED};">Training Routine · ${escapeHtml(sectionLabel || "")}</div>
        </div>

        <!-- Hero: photo + player + routine -->
        <div style="display:flex;align-items:center;gap:24px;margin-top:32px;">
          ${photoSrc ? `
          <div style="position:relative;width:120px;height:120px;flex-shrink:0;">
            <div style="position:absolute;inset:-6px;border:2px solid ${BLUE};"></div>
            <img src="${photoSrc}" style="width:120px;height:120px;object-fit:cover;background:#333;position:relative;"/>
          </div>` : ""}
          <div style="flex:1;">
            <div style="font-size:10px;letter-spacing:0.35em;text-transform:uppercase;color:${BLUE};font-weight:900;">Player</div>
            <div style="font-size:22px;font-weight:900;text-transform:uppercase;letter-spacing:-0.3px;color:${WHITE};margin-top:2px;line-height:1;">${escapeHtml(playerName || "")}</div>
            <div style="height:1px;background:${HAIR};margin:18px 0;"></div>
            <div style="font-size:10px;letter-spacing:0.35em;text-transform:uppercase;color:${BLUE};font-weight:900;">Routine</div>
            <div style="font-size:34px;font-weight:900;text-transform:uppercase;letter-spacing:-1px;color:${WHITE};margin-top:2px;line-height:1;">${escapeHtml(r.label || "Routine")}</div>
          </div>
        </div>

        <!-- Meta strip -->
        <div style="display:flex;gap:32px;margin:32px 0 24px;padding:16px 20px;background:${PANEL};border-left:3px solid ${BLUE};">
          <div>
            <div style="font-size:9px;letter-spacing:0.3em;text-transform:uppercase;color:${MUTED};">Drills</div>
            <div style="font-size:22px;font-weight:900;color:${WHITE};line-height:1;margin-top:4px;">${drillCount}</div>
          </div>
          <div style="width:1px;background:${HAIR};"></div>
          <div>
            <div style="font-size:9px;letter-spacing:0.3em;text-transform:uppercase;color:${MUTED};">Date</div>
            <div style="font-size:14px;font-weight:900;color:${WHITE};line-height:1;margin-top:6px;letter-spacing:0.05em;text-transform:uppercase;">${new Date().toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"})}</div>
          </div>
          <div style="width:1px;background:${HAIR};"></div>
          <div>
            <div style="font-size:9px;letter-spacing:0.3em;text-transform:uppercase;color:${MUTED};">Type</div>
            <div style="font-size:14px;font-weight:900;color:${BLUE};line-height:1;margin-top:6px;letter-spacing:0.05em;text-transform:uppercase;">${escapeHtml(sectionLabel || "Routine")}</div>
          </div>
        </div>

        <!-- Column headers above drill list: Drill Sequence (left) + Scoring (right) -->
        <div style="display:flex;align-items:center;gap:12px;margin-bottom:10px;">
          <div style="font-size:10px;letter-spacing:0.4em;text-transform:uppercase;color:${BLUE};font-weight:900;">Drill Sequence</div>
          <div style="flex:1;height:1px;background:${HAIR};"></div>
          <div style="font-size:10px;letter-spacing:0.4em;text-transform:uppercase;color:${GOLD};font-weight:900;min-width:140px;text-align:right;padding-right:2px;">Scoring</div>
        </div>

        ${drillsHtml || `<div style="padding:32px;text-align:center;color:${MUTED};font-style:italic;background:${PANEL};">No drills yet.</div>`}

        <!-- Footer -->
        <div style="margin-top:40px;padding-top:20px;border-top:1px solid ${HAIR};display:flex;justify-content:space-between;align-items:center;font-size:9px;letter-spacing:0.3em;text-transform:uppercase;color:${MUTED};">
          <div>Maximize Analytics · maximizeanalytics.io</div>
          <div>Player Development Sheet</div>
        </div>
      </div>
    `;
    document.body.appendChild(sheet);
    try {
      // Give the browser one paint to load the <img>
      await new Promise(res => setTimeout(res, 250));
      const canvas = await html2canvas(sheet, { backgroundColor: "#232323", scale: 2, useCORS: true, logging: false });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 36;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2;
      // Scale the whole sheet to fit ONE page (contain), centering horizontally & vertically.
      const scale = Math.min(maxW / canvas.width, maxH / canvas.height);
      const finalW = canvas.width * scale;
      const finalH = canvas.height * scale;
      const x = (pageW - finalW) / 2;
      const y = (pageH - finalH) / 2;
      pdf.addImage(img, "PNG", x, y, finalW, finalH);
      const safeName   = (playerName || "Player").replace(/\s+/g, "-");
      const safeLabel  = (r.label || "Routine").replace(/[^a-zA-Z0-9]+/g, "-");
      pdf.save(`${safeName}-${safeLabel}-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("PDF export failed"); }
    finally {
      setExporting(false);
      if (sheet.parentNode) sheet.parentNode.removeChild(sheet);
    }
  };

  return (
    <div className={embedded ? "border-2 bg-[#161616]" : "border border-white/10 bg-[#161616]"} style={embedded ? { borderColor: `${accent}99` } : undefined} data-testid={`routine-${r.id}`}>
      {/* Printable region */}
      <div ref={printRef} className="bg-[#161616]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5" style={{ background: `${accent}08` }}>
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-90"
            data-testid={`toggle-routine-${r.id}`}
          >
            {expanded
              ? <ChevronDown className="w-4 h-4 shrink-0 pdf-hide" style={{ color: accent }} />
              : <ChevronRight className="w-4 h-4 shrink-0 pdf-hide" style={{ color: accent }} />}
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.35em] mb-0.5" style={{ color: accent }}>
                {[sectionLabel, playerName].filter(Boolean).join(" · ")}
              </div>
              <div className="font-heading text-lg uppercase tracking-tight text-white truncate">{r.label}</div>
              <FocusChips focus={focus} testId={`routine-focus-${r.id}`} />
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#757575] mt-0.5 flex items-center gap-2 flex-wrap">
                <span>{r.drills?.length || 0} {r.drills?.length === 1 ? "drill" : "drills"}{r.template_id ? " · from template" : ""}</span>
                {r.video_url && <span onClick={(e) => e.stopPropagation()}><VideoLink url={r.video_url} title={r.label} testId={`routine-video-${r.id}`} /></span>}
              </div>
            </div>
          </button>
          <div className="flex items-center gap-1 shrink-0 pdf-hide" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={exportPDF}
              disabled={exporting}
              className="p-1.5 text-[#a0a0a0] hover:text-[var(--org-accent)] disabled:opacity-50"
              title="Download as PDF"
              data-testid={`download-routine-${r.id}`}
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </button>
            {canEdit && (
              <>
                {canSaveTemplate && <button onClick={onSaveTemplate} className="p-1.5 text-[#a0a0a0] hover:text-[var(--org-accent)]" title="Save to Routine Library" data-testid={`save-template-${r.id}`}>
                  <BookMarked className="w-4 h-4" />
                </button>}
                <button onClick={onEdit} className="p-1.5 text-[#a0a0a0] hover:text-white" title="Edit" data-testid={`edit-routine-${r.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={onDelete} className="p-1.5 text-[#a0a0a0] hover:text-red-400" title="Delete" data-testid={`delete-routine-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
        {expanded && (
          <div className="divide-y divide-white/5">
            {(r.drills || []).map((d, i) => (
              <div key={d.id || i} className="flex items-start gap-3 sm:gap-4 px-3 sm:px-4 py-3">
                <span className="text-[10px] text-[#575757] font-mono mt-0.5 w-5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-bold break-words">{d.name}</div>
                  {d.description && <div className="text-[11px] text-[#a0a0a0] mt-0.5 break-words">{d.description}</div>}
                  <VideoLink url={d.video_url} title={d.name} className="mt-1.5" />
                  <div className="text-[11px] font-bold uppercase tracking-[0.2em] mt-1 sm:hidden" style={{ color: accent }}>
                    {formatUnit(d.unit, d.value)}
                  </div>
                </div>
                <div className="hidden sm:block text-[11px] font-bold uppercase tracking-[0.2em] shrink-0" style={{ color: accent }}>
                  {formatUnit(d.unit, d.value)}
                </div>
              </div>
            ))}
            {(r.drills || []).length === 0 && (
              <div className="px-4 py-4 text-[12px] italic text-[#575757]">No drills yet.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Routine editor ---------------- */
function RoutineEditor({ draft, setDraft, onSave, onCancel, saving, accent, updateDraftDrill, removeDraftDrill }) {
  if (!draft) return null;
  return (
    <div className="border-2 bg-[#161616]" style={{ borderColor: `${accent}66` }} data-testid="routine-editor">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3" style={{ background: `${accent}12` }}>
        <input
          value={draft.label}
          onChange={(e) => setDraft(d => ({ ...d, label: e.target.value }))}
          placeholder="Routine label — e.g. Guard Warmup"
          className="flex-1 bg-transparent text-white font-heading text-lg uppercase tracking-tight focus:outline-none placeholder:text-[#575757] placeholder:normal-case placeholder:font-normal placeholder:text-sm"
          data-testid="routine-label-input"
          autoFocus
        />
        <button
          onClick={onSave}
          disabled={saving || !draft.label.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-[#121212] disabled:opacity-50"
          style={{ background: accent }}
          data-testid="save-routine-btn"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
        </button>
        <button onClick={onCancel} className="p-1.5 text-[#a0a0a0] hover:text-white" data-testid="cancel-routine-btn">
          <XIcon className="w-4 h-4" />
        </button>
      </div>
      <div className="px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
        <Youtube className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
        <input
          value={draft.video_url || ""}
          onChange={(e) => setDraft(d => ({ ...d, video_url: e.target.value }))}
          placeholder="YouTube link for the whole routine (optional)"
          className="flex-1 bg-transparent text-xs text-white focus:outline-none placeholder:text-[#575757]"
          data-testid="routine-video-input"
        />
      </div>
      <div className="px-4 py-2.5 border-b border-white/10" data-testid="routine-focus-editor">
        <div className="text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1.5">Focus <span className="text-[#575757] normal-case tracking-normal">· the Technical / Tactical skill this routine trains (pick as many as apply)</span></div>
        <TagEditor value={draft.tags || []} onChange={(v) => setDraft(d => ({ ...d, tags: v }))} testPrefix="routine-focus" />
      </div>
      <div className="divide-y divide-white/5">
        {draft.drills.map((d, i) => {
          const isLastBlank = i === draft.drills.length - 1 && !d.name.trim();
          return (
            <div key={i} className="flex flex-wrap items-start gap-2 px-4 py-3" data-testid={`drill-row-${i}`}>
              <GripVertical className="w-3.5 h-3.5 text-[#575757] mt-2.5 shrink-0" />
              <div className="flex-1 min-w-[180px] space-y-1.5">
                <input
                  value={d.name}
                  onChange={(e) => updateDraftDrill(i, { name: e.target.value })}
                  placeholder="Drill name"
                  className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
                  data-testid={`drill-name-${i}`}
                />
                {!isLastBlank && (
                  <>
                    <input
                      value={d.description}
                      onChange={(e) => updateDraftDrill(i, { description: e.target.value })}
                      placeholder="Description (optional)"
                      className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-1.5 text-[12px] text-[#c0c0c0] focus:outline-none focus:border-[var(--org-accent)]"
                      data-testid={`drill-desc-${i}`}
                    />
                    <div className="relative">
                      <Link2 className="w-3.5 h-3.5 text-[#575757] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        value={d.video_url || ""}
                        onChange={(e) => updateDraftDrill(i, { video_url: e.target.value })}
                        placeholder="YouTube link (optional) — demo of the drill"
                        className="w-full bg-[#0f0f0f] border border-white/10 pl-8 pr-3 py-1.5 text-[12px] text-[#c0c0c0] focus:outline-none focus:border-[var(--org-accent)]"
                        data-testid={`drill-video-${i}`}
                      />
                    </div>
                  </>
                )}
              </div>
              {!isLastBlank && (
                <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto pl-[22px] sm:pl-0">
                  <select
                    value={d.unit}
                    onChange={(e) => updateDraftDrill(i, { unit: e.target.value })}
                    className="bg-[#0f0f0f] border border-white/10 px-2 py-2 text-xs text-white shrink-0"
                    data-testid={`drill-unit-${i}`}
                  >
                    {UNIT_OPTIONS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                  <input
                    value={d.value}
                    onChange={(e) => updateDraftDrill(i, { value: e.target.value })}
                    placeholder={UNIT_OPTIONS.find(u => u.value === d.unit)?.placeholder || "…"}
                    className="w-20 bg-[#0f0f0f] border border-white/10 px-2 py-2 text-sm text-white text-center focus:outline-none focus:border-[var(--org-accent)] shrink-0"
                    data-testid={`drill-value-${i}`}
                  />
                  <button onClick={() => removeDraftDrill(i)} className="p-2 text-[#575757] hover:text-red-400 shrink-0" data-testid={`drill-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Template library dialog ---------------- */
function TemplateLibraryDialog({ onClose, onApplyToPlayer, canEdit, focusType }) {
  const focusOf = useFocus();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [byType, setByType] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listRoutineTemplates();
      setByType(r.by_type || {});
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = (routineType) => {
    setEditingId(`new:${routineType}`);
    setDraft({ routine_type: routineType, label: "", video_url: "", tags: [], drills: [blankDrill()] });
  };
  const startEdit = (t) => {
    setEditingId(t.id);
    setDraft({
      routine_type: t.routine_type,
      label: t.label,
      video_url: t.video_url || "",
      tags: Array.isArray(t.tags) ? t.tags : [],
      drills: (t.drills || []).map(d => ({ ...d })).concat([blankDrill()]),
    });
  };
  const cancel = () => { setEditingId(null); setDraft(null); };

  const updateDraftDrill = (i, patch) => {
    setDraft(d => {
      const drills = d.drills.map((x, idx) => idx === i ? { ...x, ...patch } : x);
      const last = drills[drills.length - 1];
      if (last && last.name.trim()) drills.push(blankDrill());
      return { ...d, drills };
    });
  };
  const removeDraftDrill = (i) => setDraft(d => ({ ...d, drills: d.drills.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!draft?.label.trim()) { toast.error("Label required"); return; }
    const cleaned = { ...draft, drills: draft.drills.filter(x => x.name.trim()) };
    setSaving(true);
    try {
      if (editingId?.startsWith("new:")) await api.createRoutineTemplate(cleaned);
      else await api.updateRoutineTemplate(editingId, cleaned);
      await load();
      setEditingId(null); setDraft(null);
      toast.success("Template saved");
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.label}"?`)) return;
    try { await api.deleteRoutineTemplate(t.id); await load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      <aside className="fixed right-0 top-0 h-full w-full sm:w-[720px] bg-[#151515] border-l border-white/10 z-50 flex flex-col" data-testid="template-library-dialog">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]">Routine Library</div>
            <div className="font-heading text-xl uppercase text-white">{focusType === "shooting" ? "Shooting Routines" : "Pick a routine for this player"}</div>
          </div>
          <button onClick={onClose} className="text-[#a0a0a0] hover:text-white" data-testid="template-library-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-[#a0a0a0] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (() => {
            const all = Object.values(byType).flat().filter(t => focusType ? t.routine_type === focusType : t.routine_type !== "shooting");
            const needle = query.trim().toLowerCase();
            const tpls = all.filter(t => !needle || [t.label, ...(t.drills || []).map(d => d.name), ...focusOf(t.tags).map(f => f.label)].join(" ").toLowerCase().includes(needle));
            const newKey = `new:${focusType || "pre_practice"}`;
            return (
              <div data-testid="tpl-list">
                <div className="flex items-center gap-2 mb-3">
                  <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search routines, drills, focus…" className="flex-1 bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[var(--org-accent)]" data-testid="tpl-search" />
                  {canEdit && editingId !== newKey && (
                    <button onClick={() => startNew(focusType || "pre_practice")} className="text-[10px] uppercase tracking-[0.3em] font-bold px-3 py-2 border shrink-0" style={{ color: ACCENT, borderColor: `${ACCENT}55`, background: `${ACCENT}12` }} data-testid="tpl-new">
                      <Plus className="w-3 h-3 inline mr-0.5" /> New Template
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {editingId === newKey && (
                    <RoutineEditor draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} saving={saving} accent={ACCENT} updateDraftDrill={updateDraftDrill} removeDraftDrill={removeDraftDrill} />
                  )}
                  {tpls.map(t => editingId === t.id ? (
                    <RoutineEditor key={t.id} draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} saving={saving} accent={ACCENT} updateDraftDrill={updateDraftDrill} removeDraftDrill={removeDraftDrill} />
                  ) : (
                    <TemplateCard key={t.id} t={t} accent={ACCENT} focus={focusOf(t.tags)} canEdit={canEdit} onEdit={() => startEdit(t)} onDelete={() => remove(t)} onApply={() => onApplyToPlayer(t.id)} />
                  ))}
                  {tpls.length === 0 && editingId !== newKey && (
                    <div className="text-[12px] italic text-[#575757] px-3 py-3 border border-dashed border-white/10" data-testid="tpl-empty">{all.length ? "Nothing matches." : "No routines in the library yet."}</div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </aside>
    </>
  );
}

function TemplateCard({ t, accent, focus = [], canEdit, onEdit, onDelete, onApply }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-white/10 bg-[#161616]" data-testid={`tpl-${t.id}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5" style={{ background: `${accent}08` }}>
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-90"
          data-testid={`toggle-tpl-${t.id}`}
        >
          {expanded
            ? <ChevronDown className="w-4 h-4 shrink-0" style={{ color: accent }} />
            : <ChevronRight className="w-4 h-4 shrink-0" style={{ color: accent }} />}
          <div className="min-w-0">
            <div className="font-heading text-base uppercase tracking-tight text-white truncate">{t.label}</div>
            <FocusChips focus={focus} testId={`tpl-focus-${t.id}`} />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#757575] mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{t.drills?.length || 0} drills</span>
              {t.video_url && <span onClick={(e) => e.stopPropagation()}><VideoLink url={t.video_url} title={t.label} testId={`template-video-${t.id}`} /></span>}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          <button onClick={onApply} className="px-2 py-1 border text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: accent, borderColor: `${accent}55` }} data-testid={`apply-tpl-${t.id}`}>
            <Copy className="w-3 h-3 inline mr-0.5" /> Apply to Player
          </button>
          {canEdit && (
            <>
              <button onClick={onEdit} className="p-1.5 text-[#a0a0a0] hover:text-white" data-testid={`edit-tpl-${t.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={onDelete} className="p-1.5 text-[#a0a0a0] hover:text-red-400" data-testid={`delete-tpl-${t.id}`}><Trash2 className="w-4 h-4" /></button>
            </>
          )}
        </div>
      </div>
      {expanded && (
        <div className="divide-y divide-white/5">
          {(t.drills || []).map((d, i) => (
            <div key={i} className="flex items-start gap-3 px-4 py-2 text-[12px]">
              <span className="text-[10px] text-[#575757] font-mono w-4 mt-0.5 shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="text-white truncate">{d.name}</div>
                {d.description && <div className="text-[10px] text-[#a0a0a0] mt-0.5">{d.description}</div>}
                <VideoLink url={d.video_url} title={d.name} className="mt-1" />
              </div>
              <span className="font-bold uppercase tracking-[0.15em] shrink-0" style={{ color: accent }}>{formatUnit(d.unit, d.value)}</span>
            </div>
          ))}
          {(t.drills || []).length === 0 && (
            <div className="px-4 py-3 text-[12px] italic text-[#575757]">No drills yet.</div>
          )}
        </div>
      )}
    </div>
  );
}
